export interface CurrencyMeta {
    code: string;
    symbol: string;
}

export interface CurrencyOptions {
    currencyCode?: string | undefined;
    countryCode?: string | undefined;
}

const COUNTRY_CURRENCY_MAP: Record<string, string> = {
    NG: "NGN",
    NGA: "NGN",
    GB: "GBP",
    GBR: "GBP",
    US: "USD",
    USA: "USD",
    CA: "CAD",
    CAN: "CAD",
    IE: "EUR",
    IRL: "EUR",
    ZA: "ZAR",
    ZAF: "ZAR",
    KE: "KES",
    KEN: "KES",
    GH: "GHS",
    GHA: "GHS",
};

const FALLBACK_CURRENCY: CurrencyMeta = {
    code: "NGN",
    symbol: "₦",
};

const deriveSymbol = (currencyCode: string): string => {
    try {
        const parts = new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: currencyCode,
        }).formatToParts(0);

        const currencyPart = parts.find((part) => part.type === "currency");
        return currencyPart?.value || currencyCode;
    } catch (error) {
        return currencyCode;
    }
};

const normalizeCountryCode = (countryCode?: string | null): string => {
    if (!countryCode || typeof countryCode !== "string") {
        return "";
    }
    return countryCode.trim().toUpperCase();
};

const lookupCurrencyByCountry = (countryCode?: string | null): string | null => {
    const code = normalizeCountryCode(countryCode);
    if (!code) {
        return null;
    }

    const mapped = COUNTRY_CURRENCY_MAP[code];
    if (mapped) {
        return mapped;
    }

    if (code.length === 3) {
        const iso2Candidate = code.slice(0, 2);
        const iso2Mapped = COUNTRY_CURRENCY_MAP[iso2Candidate];
        if (iso2Mapped) {
            return iso2Mapped;
        }
    }

    return null;
};

export const getCurrencyMeta = ({ currencyCode, countryCode }: CurrencyOptions = {}): CurrencyMeta => {
    if (currencyCode && typeof currencyCode === "string") {
        const code = currencyCode.trim().toUpperCase();
        return {
            code,
            symbol: deriveSymbol(code),
        };
    }

    const resolvedCurrency = lookupCurrencyByCountry(countryCode);
    if (resolvedCurrency) {
        return {
            code: resolvedCurrency,
            symbol: deriveSymbol(resolvedCurrency),
        };
    }

    return { ...FALLBACK_CURRENCY };
};

export const buildCurrencyOptions = (
    currencyCode?: string | null,
    countryCode?: string | null
): CurrencyOptions => {
    const options: CurrencyOptions = {};
    if (typeof currencyCode === "string" && currencyCode.trim()) {
        options.currencyCode = currencyCode.trim();
    }
    if (typeof countryCode === "string" && countryCode.trim()) {
        options.countryCode = countryCode.trim();
    }
    return options;
};

export const formatCurrency = (amount: number | string | null | undefined, options: CurrencyOptions = {}): string => {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) {
        return amount?.toString() ?? "";
    }

    const { code, symbol } = getCurrencyMeta(options);
    const hasDecimal = Math.abs(numericAmount % 1) > 0;

    try {
        return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: code,
            minimumFractionDigits: hasDecimal ? 2 : 0,
            maximumFractionDigits: 2,
        }).format(numericAmount);
    } catch (error) {
        const formattedValue = numericAmount.toLocaleString(undefined, {
            minimumFractionDigits: hasDecimal ? 2 : 0,
            maximumFractionDigits: 2,
        });
        return `${symbol}${formattedValue}`;
    }
};

export const __internal = {
    COUNTRY_CURRENCY_MAP,
    FALLBACK_CURRENCY,
    lookupCurrencyByCountry,
    normalizeCountryCode,
};
